<template>
  <td
    class="themed-table-cell"
    :class="[
      column.class,
      {
        'themed-table-editable': editable && !editing,
        'themed-table-editing': editing && editable,
        [`themed-table-align-${column.align}`]: column.align
      }
    ]"
    :style="getCellStyle()"
    role="cell"
    :aria-label="getCellAriaLabel()"
    @click="handleCellClick"
  >
    <!-- Edit mode - only show edit UI for editable columns when row is in edit mode -->
    <div v-if="editing && editable" class="themed-table-cell-edit-container">
      <!-- Text input for text type -->
      <themed-input
        v-if="!column.type || column.type === 'text'"
        ref="editInput"
        :value="currentEditValue"
        :error="hasValidationError"
        :aria-label="`Edit ${column.label || column.field}`"
        :aria-describedby="validationError ? `error-${column.field}` : null"
        @input="handleInputChange"
        @keydown.enter="handleSaveRow"
        @keydown.esc="handleCancelRow"
      />
      
      <!-- Number input for number type -->
      <themed-input
        v-else-if="column.type === 'number'"
        ref="editInput"
        type="number"
        :value="currentEditValue"
        :error="hasValidationError"
        :aria-label="`Edit ${column.label || column.field}`"
        :aria-describedby="validationError ? `error-${column.field}` : null"
        @input="handleInputChange"
        @keydown.enter="handleSaveRow"
        @keydown.esc="handleCancelRow"
      />
      
      <!-- Date input for date type -->
      <themed-input
        v-else-if="column.type === 'date'"
        ref="editInput"
        type="date"
        :value="currentEditValue"
        :error="hasValidationError"
        :aria-label="`Edit ${column.label || column.field}`"
        :aria-describedby="validationError ? `error-${column.field}` : null"
        @input="handleInputChange"
        @keydown.enter="handleSaveRow"
        @keydown.esc="handleCancelRow"
      />
      
      <!-- Select dropdown for select type -->
      <themed-select
        v-else-if="column.type === 'select'"
        ref="editInput"
        :value="currentEditValue"
        :options="column.options || []"
        :error="hasValidationError"
        :aria-label="`Edit ${column.label || column.field}`"
        :aria-describedby="validationError ? `error-${column.field}` : null"
        @input="handleInputChange"
        @keydown.esc="handleCancelRow"
      />
      
      <!-- Checkbox/Toggle for checkbox type -->
      <div v-else-if="column.type === 'checkbox'" class="themed-table-cell-checkbox-edit">
        <label class="inline-flex items-center gap-2">
          <input 
            type="checkbox" 
            :checked="currentEditValue"
            class="form-checkbox"
            @change="handleCheckboxChange"
          />
          <span>{{ currentEditValue ? 'Active' : 'Inactive' }}</span>
        </label>
      </div>
      
      <!-- Fallback to text input -->
      <themed-input
        v-else
        ref="editInput"
        :value="currentEditValue"
        :error="hasValidationError"
        :aria-label="`Edit ${column.label || column.field}`"
        :aria-describedby="validationError ? `error-${column.field}` : null"
        @input="handleInputChange"
        @keydown.enter="handleSaveRow"
        @keydown.esc="handleCancelRow"
      />
      
    </div>
    
    <!-- Display mode -->
    <template v-else>
      <!-- Column-specific slot (highest priority) -->
      <slot 
        v-if="hasColumnSlot"
        :name="`column-${column.field}`"
        :row="row"
        :value="value"
        :column="column"
      ></slot>
      
      <!-- Actions slot (for actions column type) -->
      <slot
        v-else-if="column.type === 'actions' && hasActionsSlot"
        name="actions"
        :row="row"
      ></slot>
      
      <!-- Custom component rendering -->
      <component
        v-else-if="column.component"
        :is="column.component"
        :value="value"
        :row="row"
        :column="column"
        v-bind="column.componentProps || {}"
      />
      
      <!-- Formatter (for custom formatting) -->
      <span v-else-if="column.formatter" v-html="column.formatter(value, row)">
      </span>
      
      <!-- Type-based rendering with optional badge wrapper -->
      <template v-else>
        <!-- Text type (default) -->
        <span 
          v-if="!column.type || column.type === 'text'"
          :class="column.badge ? getBadgeClass(value, row, column) : null"
        >
          {{ getTransformedValue(value, column) }}
        </span>
        
        <!-- Number type -->
        <span 
          v-else-if="column.type === 'number'" 
          :class="column.badge ? getBadgeClass(value, row, column) : 'themed-table-cell-number'"
        >
          {{ formatNumber(value) }}
        </span>
        
        <!-- Currency type -->
        <span 
          v-else-if="column.type === 'currency'" 
          :class="[
            column.shorthand ? 'is-shorthand-currency' : 'is-currency',
            column.badge ? getBadgeClass(value, row, column) : 'themed-table-cell-currency'
          ]"
          :data-originalvalue="getOriginalCurrencyValue(value, column)"
          :data-precision="column.precision !== undefined ? column.precision : 2"
        >
          {{ formatCurrencyValue(value, column) }}
        </span>
        
        <!-- Percentage type -->
        <span 
          v-else-if="column.type === 'percentage'" 
          :class="column.badge ? getBadgeClass(value, row, column) : 'themed-table-cell-percentage'"
        >
          {{ formatPercentageValue(value, column) }}
        </span>
        
        <!-- Date type -->
        <span 
          v-else-if="column.type === 'date'" 
          :class="column.badge ? getBadgeClass(value, row, column) : 'themed-table-cell-date'"
        >
          {{ formatDate(value) }}
        </span>
        
        <!-- Image type -->
        <img 
          v-else-if="column.type === 'image'"
          :src="value"
          :alt="column.alt || getImageAlt()"
          :class="['themed-table-cell-image', column.imageClass]"
          :style="column.imageStyle"
          loading="lazy"
        />
        
        <!-- Checkbox type - show as badge -->
        <themed-badge
          v-else-if="column.type === 'checkbox'"
          :variant="value ? 'default' : 'secondary'"
        >
          {{ value ? 'Active' : 'Inactive' }}
        </themed-badge>
        
        <!-- Select type -->
        <span v-else-if="column.type === 'select'" class="themed-table-cell-select">
          {{ getSelectLabel(value, column.options) }}
        </span>
        
        <!-- Actions type -->
        <div v-else-if="column.type === 'actions'" class="themed-table-cell-actions">
          <!-- Edit mode actions: Save and Cancel buttons -->
          <template v-if="editing">
            <themed-button
              variant="default"
              size="sm"
              :disabled="saving"
              :aria-label="'Save changes'"
              @click.stop="handleSaveRow"
            >
              <span>{{ saving ? 'Saving...' : 'Save' }}</span>
            </themed-button>
            <themed-button
              variant="ghost"
              size="sm"
              :disabled="saving"
              :aria-label="'Cancel editing'"
              @click.stop="handleCancelRow"
            >
              <span>Cancel</span>
            </themed-button>
          </template>
          
          <!-- View mode actions: Info button + Edit button (if any columns are editable) + configured action buttons -->
          <template v-else>
            <!-- Info button to show row details (if enabled) -->
            <themed-button
              v-if="showRowDetails"
              variant="ghost"
              size="sm"
              :aria-label="`View details for row ${rowIndex + 1}`"
              @click.stop="$emit('show-details')"
            >
              <theme-icon icon="info-icon" type="icons" classes="flex" />
            </themed-button>
            
            <!-- Auto-generated Edit button if any columns are editable -->
            <themed-button
              v-if="hasEditableColumns"
              variant="ghost"
              size="sm"
              :aria-label="'Edit row'"
              @click.stop="$emit('start-edit-row')"
            >
              <span>Edit</span>
            </themed-button>
            
            <!-- Configured action buttons -->
            <themed-button
              v-for="(action, actionIndex) in getVisibleActions()"
              :key="actionIndex"
              :variant="action.variant || 'ghost'"
              :size="action.size || 'sm'"
              :disabled="action.disabled"
              :class="getActionClass(action)"
              :aria-label="getActionLabel(action)"
              @click.stop="handleActionClick(action)"
            >
              <theme-icon
                v-if="getActionIcon(action)"
                :icon="getActionIcon(action)"
                type="icons"
                classes="w-4 h-4"
              />
              <span v-if="getActionLabel(action) && !action.iconOnly">{{ getActionLabel(action) }}</span>
            </themed-button>
          </template>
        </div>
        
        <!-- Fallback to text -->
        <span v-else>
          {{ value }}
        </span>
      </template>
    </template>
  </td>
</template>

<script>
import ThemedBadge from '../ThemedBadge.vue';
import ThemedCheckbox from '../ThemedCheckbox.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import { formatNumber, formatCurrencyUnits, formatShorthandCurrency, formatDate, formatPercentage } from '../../../../utils/formatters';

export default {
  name: 'TableCell',
  components: {
    ThemedBadge,
    ThemedCheckbox,
    ThemedInput,
    ThemedSelect,
    ThemedButton,
    ThemeIcon
  },
  props: {
    column: {
      type: Object,
      required: true
    },
    row: {
      type: Object,
      required: true
    },
    rowIndex: {
      type: Number,
      default: -1
    },
    value: {
      type: null,
      default: null
    },
    editable: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    editValue: {
      type: null,
      default: null
    },
    hasColumnSlot: {
      type: Boolean,
      default: false
    },
    hasActionsSlot: {
      type: Boolean,
      default: false
    },
    hasEditableColumns: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    columnWidth: {
      type: Number,
      default: null
    },
    showRowDetails: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: String,
      default: 'row'
    }
  },
  data() {
    return {
      validationError: null
    };
  },
  computed: {
    hasValidationError() {
      return !!this.validationError;
    },
    
    currentEditValue() {
      // Use editValue prop when in edit mode, otherwise use value prop
      return this.editing && this.editable ? this.editValue : this.value;
    },
    
    formattedValue() {
      // Apply formatter if provided
      if (this.column.formatter && typeof this.column.formatter === 'function') {
        return this.column.formatter(this.value, this.row);
      }
      return this.value;
    }
  },
  watch: {
    editing(newVal) {
      if (newVal && this.editable) {
        // Entering edit mode - clear validation errors
        this.validationError = null;
        
        // Focus the input on next tick (only for first editable column)
        this.$nextTick(() => {
          if (this.$refs.editInput) {
            const input = this.$refs.editInput.$el || this.$refs.editInput;
            if (input && input.focus) {
              input.focus();
              // Select text for easier editing
              if (input.select) {
                input.select();
              }
            }
          }
        });
      }
    }
  },
  methods: {
    getTransformedValue(value, column) {
      // Apply transform function if specified
      if (column.transform && typeof column.transform === 'function') {
        return column.transform(value);
      }
      return value;
    },
    
    getCellAriaLabel() {
      // Build descriptive ARIA label for the cell
      const columnLabel = this.column.label || this.column.field || 'Cell';
      const cellValue = this.value !== null && this.value !== undefined ? String(this.value) : 'empty';
      
      if (this.editable && !this.editing) {
        return `${columnLabel}: ${cellValue}. Click to edit.`;
      } else if (this.editing) {
        return `Editing ${columnLabel}`;
      } else {
        return `${columnLabel}: ${cellValue}`;
      }
    },
    
    handleInputChange(value) {
      // Emit event to update the edit value in parent
      this.$emit('update-edit-value', this.column.field, value);
    },
    
    handleCheckboxChange(event) {
      // Emit event to update the checkbox value in parent
      this.$emit('update-edit-value', this.column.field, event.target.checked);
    },
    
    handleSaveRow() {
      // Emit event to save the entire row (for row/toggle/bulk modes)
      if (this.editMode === 'cell') {
        this.$emit('save-edit-cell');
      } else {
        this.$emit('save-edit-row');
      }
    },
    
    handleCancelRow() {
      // Emit event to cancel editing the entire row
      if (this.editMode === 'cell') {
        this.$emit('cancel-edit-cell');
      } else {
        this.$emit('cancel-edit-row');
      }
    },
    
    validateValue(value) {
      // Clear previous validation error
      this.validationError = null;
      
      // Check if column has validation rules
      if (!this.column.validation) {
        return true;
      }
      
      const validation = this.column.validation;
      
      // Handle string validation (simple required check)
      if (typeof validation === 'string') {
        if (validation === 'required' && (value === null || value === undefined || value === '')) {
          this.validationError = 'This field is required';
          return false;
        }
      }
      
      // Handle object validation (more complex rules)
      if (typeof validation === 'object') {
        // Required validation
        if (validation.required && (value === null || value === undefined || value === '')) {
          this.validationError = validation.message || 'This field is required';
          return false;
        }
        
        // Min length validation
        if (validation.minLength && String(value).length < validation.minLength) {
          this.validationError = validation.message || `Minimum length is ${validation.minLength}`;
          return false;
        }
        
        // Max length validation
        if (validation.maxLength && String(value).length > validation.maxLength) {
          this.validationError = validation.message || `Maximum length is ${validation.maxLength}`;
          return false;
        }
        
        // Min value validation (for numbers)
        if (validation.min !== undefined && Number(value) < validation.min) {
          this.validationError = validation.message || `Minimum value is ${validation.min}`;
          return false;
        }
        
        // Max value validation (for numbers)
        if (validation.max !== undefined && Number(value) > validation.max) {
          this.validationError = validation.message || `Maximum value is ${validation.max}`;
          return false;
        }
        
        // Pattern validation (regex)
        if (validation.pattern) {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(String(value))) {
            this.validationError = validation.message || 'Invalid format';
            return false;
          }
        }
      }
      
      // Handle function validation (custom validator)
      if (typeof validation === 'function') {
        const result = validation(value, this.row);
        
        if (result === false) {
          this.validationError = 'Validation failed';
          return false;
        }
        
        if (typeof result === 'string') {
          this.validationError = result;
          return false;
        }
      }
      
      return true;
    },
    
    getCellStyle() {
      const styles = {};
      
      // Apply dynamic width from columnWidth prop (takes priority)
      if (this.columnWidth) {
        styles.width = `${this.columnWidth}px`;
        styles.minWidth = `${this.columnWidth}px`;
        styles.maxWidth = `${this.columnWidth}px`;
      }
      // Apply column width if specified in column config
      else if (this.column.width) {
        styles.width = this.column.width;
      }
      
      return styles;
    },
    
    handleCellClick() {
      // Don't trigger edit on actions column
      if (this.column.type === 'actions') {
        return;
      }
      
      // Start editing based on edit mode
      if (this.editable && !this.editing) {
        if (this.editMode === 'cell') {
          // Cell mode - edit only this cell
          this.$emit('start-edit-cell');
        } else {
          // Row/toggle/bulk mode - edit entire row
          this.$emit('start-edit-row');
        }
      }
    },
    
    getBadgeVariant(value) {
      // Default badge variant logic based on value
      // Can be overridden by column.variant
      if (!value) {
        return 'outline';
      }
      
      const lowerValue = String(value).toLowerCase();
      
      // Common status mappings
      if (lowerValue.includes('active') || lowerValue.includes('success') || lowerValue.includes('approved')) {
        return 'default';
      }
      if (lowerValue.includes('pending') || lowerValue.includes('warning')) {
        return 'secondary';
      }
      if (lowerValue.includes('error') || lowerValue.includes('failed') || lowerValue.includes('rejected')) {
        return 'destructive';
      }
      
      return 'outline';
    },
    
    getBadgeClass(value, row, column) {
      // Base badge classes
      const baseClasses = 'themed-table-badge';
      
      // If column has badgeClass function, use it
      if (column.badgeClass && typeof column.badgeClass === 'function') {
        const customClass = column.badgeClass(value, row);
        return customClass ? `${baseClasses} ${customClass}` : baseClasses;
      }
      
      // If column has static badgeClass string, use it
      if (column.badgeClass && typeof column.badgeClass === 'string') {
        return `${baseClasses} ${column.badgeClass}`;
      }
      
      // Default badge class
      return baseClasses;
    },
    
    formatCurrencyValue(value, column) {
      // Apply transform function if specified (e.g., convert cents to dollars)
      let amount = value;
      if (column.transform && typeof column.transform === 'function') {
        amount = column.transform(value);
      } else if (column.shorthand) {
        // For shorthand currency without explicit transform, assume value is in cents
        amount = value / 100;
      }
      
      // Check if column specifies shorthand format
      if (column.shorthand) {
        return formatShorthandCurrency(amount);
      }
      
      // Use precision from column config, default to 2
      // Whole units, so a cents-valued column needs a transform or shorthand above.
      const precision = column.precision !== undefined ? column.precision : 2;
      return formatCurrencyUnits(amount, precision);
    },
    
    getOriginalCurrencyValue(value, column) {
      // For shorthand currency, ThemedCurrencyConversion expects the shorthand format like "10.7M"
      // For regular currency, it expects the raw value in cents
      
      if (column.shorthand) {
        // For shorthand, we need to convert cents to dollars first
        // Shorthand values are typically large, so they're stored in cents
        let amount = value / 100; // Convert cents to dollars
        
        // Apply additional transform if specified
        if (column.transform && typeof column.transform === 'function') {
          amount = column.transform(value);
        }
        
        // Format to shorthand and extract just the number + suffix (no currency symbol)
        const formatted = formatShorthandCurrency(amount);
        // Remove currency symbol but keep number and suffix (K, M, B)
        return formatted.replace(/[^0-9.KMB]/g, '');
      }
      
      // For regular currency, return the raw value in cents (no transform)
      return value;
    },
    
    formatPercentageValue(value, column) {
      // Use precision from column config, default to 0
      const precision = column.precision !== undefined ? column.precision : 0;
      return formatPercentage(value, precision);
    },
    
    // Expose formatters for template use
    formatNumber,
    formatDate,
    
    handleCheckboxToggle(checked) {
      // Emit change event for checkbox type in display mode
      this.$emit('update', this.row, this.column.field, checked);
    },
    
    getImageAlt() {
      // Generate alt text from row data if available
      if (this.row && this.column.altField) {
        return this.row[this.column.altField] || 'Image';
      }
      return this.column.label || 'Image';
    },
    
    getSelectLabel(value, options) {
      if (!options || !Array.isArray(options)) {
        return value;
      }
      
      // Find the option that matches the value
      const option = options.find(opt => {
        // Support both simple arrays and object arrays
        if (typeof opt === 'string' || typeof opt === 'number') {
          return opt === value;
        }
        
        // Object format: { label, value } or { text, value } or { name, value }
        const optValue = opt.value !== undefined ? opt.value : opt;
        return optValue === value;
      });
      
      if (!option) {
        return value;
      }
      
      // Return the label
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      
      return option.label || option.text || option.name || option.value || value;
    },
    
    getVisibleActions() {
      // Get actions from column configuration
      if (!this.column.buttons || !Array.isArray(this.column.buttons)) {
        return [];
      }
      
      // Filter actions based on conditional visibility
      return this.column.buttons.filter(action => {
        // If no visible property, action is always visible
        if (action.visible === undefined) {
          return true;
        }
        
        // If visible is a boolean, use it directly
        if (typeof action.visible === 'boolean') {
          return action.visible;
        }
        
        // If visible is a function, call it with the row data
        if (typeof action.visible === 'function') {
          return action.visible(this.row);
        }
        
        return true;
      });
    },
    
    handleActionClick(action) {
      // Emit action event with action name and row data
      this.$emit('action', action.name, this.row);
    },
    
    getActionLabel(action) {
      // Support dynamic labels via function
      if (typeof action.label === 'function') {
        return action.label(this.row);
      }
      return action.label;
    },
    
    getActionIcon(action) {
      // Support dynamic icons via function
      if (typeof action.icon === 'function') {
        return action.icon(this.row);
      }
      return action.icon;
    },
    
    getActionClass(action) {
      // Support dynamic classes via function
      if (typeof action.class === 'function') {
        return action.class(this.row);
      }
      return action.class;
    }
  }
}
</script>
